import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, FormControl, InputLabel, Select, MenuItem, Box, Alert,
} from '@mui/material';
import EmployeeAutocomplete, { EmployeeOption } from '../common/EmployeeAutocomplete';
import { helpWhy } from '../../help/why';
import { ExitType, ICreateEmployeeExitRequest } from '../../interfaces/payroll.interface';

// DISMISSAL (termination benefits REV 2 D3) added -- dismissal for misconduct under §23, distinct
// from TERMINATION (§26 termination simpliciter).
const EXIT_TYPES: ExitType[] = [
    'RESIGNATION', 'TERMINATION', 'DISCHARGE', 'RETRENCHMENT', 'RETIREMENT', 'DEATH', 'ABSCONDING',
    'DISMISSAL',
];

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: (data: ICreateEmployeeExitRequest) => void;
    saving?: boolean;
}

// Create dialog (spec §3): employee picker, exit type select, last working day (required) /
// notice date (optional — an absconding exit has none) date pickers, remarks. No date-order
// constraint is enforced client-side, matching spec §2.4's own refusal (none).
const EmployeeExitFormDialog: React.FC<Props> = ({ open, onClose, onSave, saving = false }) => {
    const [employee, setEmployee] = useState<EmployeeOption | null>(null);
    const [exitType, setExitType] = useState<ExitType>('RESIGNATION');
    const [lastWorkingDay, setLastWorkingDay] = useState('');
    const [noticeDate, setNoticeDate] = useState('');
    const [remarks, setRemarks] = useState('');

    useEffect(() => {
        if (open) {
            setEmployee(null);
            setExitType('RESIGNATION');
            setLastWorkingDay('');
            setNoticeDate('');
            setRemarks('');
        }
    }, [open]);

    const isValid = !!employee && !!lastWorkingDay;

    const createWhy = saving ? 'Saving is in progress.'
        : !employee ? 'Choose an employee first.'
        : !lastWorkingDay ? 'Enter the last working day first.' : null;

    const handleSubmit = () => {
        if (!isValid || !employee) return;
        onSave({
            userId: employee.id,
            exitType,
            lastWorkingDay,
            noticeDate: noticeDate || null,
            remarks: remarks.trim() || null,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'payroll.exitForm.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>New Employee Exit</DialogTitle>
            <DialogContent data-help-section="payroll.exitForm.section.details">
                <Box sx={{ mt: 1, mb: 2 }}>
                    <EmployeeAutocomplete
                        value={employee}
                        onChange={setEmployee}
                        data-help="payroll.exitForm.field.employee" label="Employee"
                        required
                        fullWidth
                    />
                </Box>
                <FormControl fullWidth sx={{ mb: 2 }}>
                    <InputLabel id="employee-exit-type-label">Exit Type</InputLabel>
                    <Select
                        data-help="payroll.exitForm.field.exitType" labelId="employee-exit-type-label"
                        label="Exit Type"
                        value={exitType}
                        onChange={(e) => setExitType(e.target.value as ExitType)}
                    >
                        {EXIT_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>{t}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {exitType === 'DISMISSAL' && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Dismissal for misconduct (§23): the settlement's statutory compensation
                        defaults to the §23(2) 15 days/year scale, without notice pay. Forfeiting
                        the whole service benefit for a §23 proviso case is a separate, explicit
                        choice made when computing the settlement.
                    </Alert>
                )}
                <TextField
                    data-help="payroll.exitForm.field.lastDay" label="Last Working Day"
                    type="date"
                    value={lastWorkingDay}
                    onChange={(e) => setLastWorkingDay(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                    fullWidth
                    sx={{ mb: 2 }}
                />
                <TextField
                    data-help="payroll.exitForm.field.noticeDate" label="Notice Date"
                    type="date"
                    value={noticeDate}
                    onChange={(e) => setNoticeDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={{ mb: 2 }}
                />
                <TextField
                    data-help="payroll.exitForm.field.remarks" label="Remarks"
                    multiline
                    minRows={2}
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    fullWidth
                />
            </DialogContent>
            <DialogActions>
                <Button data-help="payroll.exitForm.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button data-help="payroll.exitForm.save" {...helpWhy(createWhy ? { kind: 'status', message: createWhy } : null)} onClick={handleSubmit} variant="contained" disabled={!isValid || saving}>
                    Create
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmployeeExitFormDialog;
